import AddProcessBar from "@/components/add-process-bar"
import Button from "@/components/button"
import EndShiftErrorSheet from "@/components/end-shift-error-sheet"
import EndShiftSheet from "@/components/end-shift-sheet"
import RoundedButton from "@/components/rounded-button"
import SelectProcessSheet from "@/components/select-process-sheet"
import Topbar from "@/components/topbar"
import { Skeleton } from "@/components/ui/skeleton"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import { Icon } from "@/icons/icon"
import DraftCard from "@/ipqc/components/draft-card"
import { useEnsureBoiloutOfflineBundle } from "@/hooks/use-ensure-boilout-offline-bundle"
import { useEnsureIpqcOfflineBundle } from "@/hooks/use-ensure-ipqc-offline-bundle"
import { posthog } from "@/lib/posthog"
import { EnumsOf, formatShiftTime, useDisableBack } from "@/lib/utils"
import { handleNetworkError, handleOfflineActionError } from "@/services/base"
import { getAllDeptSchemas } from "@/services/packaging"
import { getAllV2DeptSchemas, getSessionSheets } from "@/services/packaging-v2"
import {
  getProducts as getRMPMProducts,
  getSessionSheets as getRMPMSessionSheets,
  hydrateFromSessionSheets as hydrateRMPMFromSessionSheets,
} from "@/services/rmpm"
import {
  addProcesses,
  discardShift,
  endShift,
  getShiftStatus,
  mergeSession,
} from "@/services/session"
import {
  ensureOfflineBundleAvailable as ensureBoiloutOfflineBundleAvailable,
  finalizePendingOfflineBoiloutProcess,
  getBoiloutOfflineBundleErrorMessage,
  hydrateOnlineBoiloutSession,
} from "@/services/boilout"
import {
  ensureOfflineBundleAvailable,
  finalizePendingOfflineIpqcProcess,
  getIpqcOfflineBundleErrorMessage,
  hydrateOnlineIpqcSession,
  SuggestedEmployee,
} from "@/services/ipqc"
import LogoutConfirmSheet from "@/components/logout-confirm-sheet"
import useStore from "@/store"
import { ProcessSession, ShiftStatus } from "@/store/session"
import { HTTPError } from "ky"
import { difference } from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

type RawData = Record<string, any>

interface PendingLine {
  parameterId: string
  label: string
}

interface Product {
  name: string
  productId: string
  pendingLines?: PendingLine[]
}

interface Truck {
  truckNumber: string
  sheetId: string
  products: Product[]
  generalPendingLines?: PendingLine[]
}

interface SummaryItem {
  name: string
  Pending: string
}

export interface RMPMPendingSummary {
  Name: string
  items: SummaryItem[]
}

export interface ShiftRMPMData {
  PMTYPE: string
  truck: Truck[]
}

export const ManageProcessesSheets = {
  EndShift: "EndShift",
  None: "None",
  EndShiftError: "EndShiftError",
  DiscardShift: "DiscardShift",
} as const

export type ManageProcessesSheets = EnumsOf<typeof ManageProcessesSheets>

export default function ManageProcesses() {
  const navigate = useNavigate()
  const userStore = useStore.use.user()
  const sessionStore = useStore.use.session()
  const packagingStore = useStore.use.packaging()
  const packagingV2Store = useStore.use.packagingV2()
  const rmpmStore = useStore.use.rmpm()
  const ipqcStore = useStore.use.ipqc()
  const boiloutStore = useStore.use.boilout()
  const [showProcessSheet, setShowProcessSheet] = useState(false)
  const [sheetPendingLine, setSheetPendingLine] = useState<
    RMPMPendingSummary[]
  >([])
  const [rmpmEmployees, setRmPmEmployees] = useState<SuggestedEmployee[]>([])
  const [currSheet, setCurrSheet] = useState<ManageProcessesSheets>(
    ManageProcessesSheets.None
  )
  const [sheetText, setSheetText] = useState({
    title: "",
    text: "",
  })
  const [showLogoutSheet, setShowLogoutSheet] = useState(false)
  const hasIpqcProcess = sessionStore.session.processes.some(
    (process) => process.processName === "In-Process QC"
  )
  const hasBoiloutProcess = sessionStore.session.processes.some(
    (process) => process.processName === "Boilout"
  )

  useDisableBack()
  const isLoadingSession = useInit()
  useEnsureIpqcOfflineBundle({
    enabled:
      sessionStore.session.status === ShiftStatus.Started && hasIpqcProcess,
  })
  useEnsureBoiloutOfflineBundle({
    enabled:
      sessionStore.session.status === ShiftStatus.Started && hasBoiloutProcess,
  })

    function useInit() {
      const [isLoadingSession, setIsLoadingSession] = useState(false)

      async function init() {
        try {
        const pendingIpqcDiscardProcessId =
          sessionStore.pendingIpqcDiscardProcessId
        const pendingBoiloutDiscardProcessId =
          sessionStore.pendingBoiloutDiscardProcessId

        // Always fetch and merge fresh session data from server
        // The merge strategy preserves local additions while updating server-provided fields
        // This ensures status updates are reflected without losing local-only changes
        setIsLoadingSession(true)

        const isOnline =
          typeof navigator === "undefined" ? true : navigator.onLine !== false

        if (isOnline) {
          try {
            await finalizePendingOfflineIpqcProcess()
          } catch (finalizeErr) {
            console.error(
              "Failed to finalize pending offline IPQC process:",
              finalizeErr
            )
            posthog.captureException(finalizeErr)
            toast.error(
              "Failed to sync the ended IPQC process. Showing current server status."
            )
          }

          try {
            await finalizePendingOfflineBoiloutProcess()
          } catch (finalizeErr) {
            console.error(
              "Failed to finalize pending offline Boilout process:",
              finalizeErr
            )
            posthog.captureException(finalizeErr)
            toast.error(
              "Failed to sync the ended Boilout process. Showing current server status."
            )
          }
        }

        const status = await getShiftStatus()

        if (status.shiftSession?.employeeSessions) {
          if (pendingIpqcDiscardProcessId) {
            const hasPendingProcess = status.shiftSession.employeeSessions.some(
              (process) => process.processId === pendingIpqcDiscardProcessId
            )

            if (hasPendingProcess) {
              status.shiftSession.employeeSessions =
                status.shiftSession.employeeSessions.filter(
                  (process) => process.processId !== pendingIpqcDiscardProcessId
                )
            } else {
              useStore.getState().session.clearPendingIpqcDiscard()
            }
          }

          if (pendingBoiloutDiscardProcessId) {
            const hasPendingProcess = status.shiftSession.employeeSessions.some(
              (process) => process.processId === pendingBoiloutDiscardProcessId
            )

            if (hasPendingProcess) {
              status.shiftSession.employeeSessions =
                status.shiftSession.employeeSessions.filter(
                  (process) => process.processId !== pendingBoiloutDiscardProcessId
                )
            } else {
              useStore.getState().session.clearPendingBoiloutDiscard()
            }
          }
        }

        // Use merge instead of setSession to preserve local changes
        mergeSession(status.shiftSession)
      } catch (err) {
        if (
          err instanceof Error &&
          err.name === "TypeError" &&
          sessionStore.shift.id &&
          sessionStore.session.status === ShiftStatus.Started
        ) {
          console.warn("Using locally persisted shift state in offline mode")
          return
        }

        console.error(err)
        posthog.captureException(err)
        toast.error("Failed to load session. Please try reloading.")
      } finally {
        setIsLoadingSession(false)
      }
    }

    useEffect(() => {
      void init()
    }, [])

    return isLoadingSession
  }

  function topbarSubtitle() {
    return `${userStore.employee.name} (${userStore.plant.name})`
  }

  function canAddProcess() {
    const diff = difference(
      userStore.processes.map((i: { id: any }) => i.id),
      dedupedProcesses.map((i: { processId: any }) => i.processId)
    )

    if (diff.length) {
      return true
    }

    return false
  }

  const dedupedProcesses = useMemo(() => {
    const latestByProcessId = new Map<string, ProcessSession>()

    for (const process of sessionStore.session.processes) {
      const existing = latestByProcessId.get(process.processId)

      if (!existing) {
        latestByProcessId.set(process.processId, process)
        continue
      }

      const existingTs = new Date(
        existing.updatedAt || existing.endedAt || existing.startedAt || 0
      ).getTime()
      const processTs = new Date(
        process.updatedAt || process.endedAt || process.startedAt || 0
      ).getTime()

      if (processTs >= existingTs) {
        latestByProcessId.set(process.processId, process)
      }
    }

    return Array.from(latestByProcessId.values())
  }, [sessionStore.session.processes])

  async function handleDiscardShift() {
    try {
      const session = await getShiftStatus().then((res) => res.shiftSession)
      if (session?.employeeSessions?.length) {
        for (const process of session.employeeSessions) {
          if (process.status !== ShiftStatus.Ended) {
            setSheetText({
              title: "Discard Shift Error",
              text: "Please end or discard all the processes before discarding the shift",
            })
            setCurrSheet(ManageProcessesSheets.EndShiftError)

            return
          }
        }
      }

      await discardShift()
      await navigate("/onboard-shift")
    } catch (err) {
      posthog.captureException(err)

      if (handleOfflineActionError(err, { action: "discard", target: "shift" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error("Error discarding shift")
      console.error(err)
    }
  }

  function handleTriggerDiscardShift() {
    setSheetText({
      title: "Discard Shift",
      text: "Are you sure you want to Discard and Exit? This will permanently delete your current shift data.",
    })
    setCurrSheet(ManageProcessesSheets.DiscardShift)
  }

  async function handleTriggerAddProcess() {
    setShowProcessSheet(true)
  }

  async function handleStartProcess(processId: string) {
    try {
      const processSession = sessionStore.session.processes.find(
        (process: { processId: string }) => process.processId === processId
      )
      if (!processSession) {
        posthog.captureException(new Error("Selected process not found"))
        toast.error("Selected process not found")

        return
      }

      if (processSession.processName === "In-Process QC") {
        let bundle
        try {
          bundle = await ensureOfflineBundleAvailable({
            isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
          })
        } catch (err) {
          toast.error(getIpqcOfflineBundleErrorMessage(err))
          return
        }

        if (processSession.status === ShiftStatus.Inactive) {
          try {
            ipqcStore.setShiftDetails({
              processId: processSession.processId,
              shiftId: sessionStore.shift.id,
              products: bundle.products.map((p) => ({
                id: p.id,
                name: p.name,
              })),
            })
            startProcess(processId)
            await navigate("/ipqc/select-products")
          } catch (err) {
            console.error("Failed to prepare offline IPQC bundle:", err)
            throw err
          }
        } else {
          ipqcStore.setShiftDetails({
            id: sessionStore.shift.id,
            processId,
            shiftId: sessionStore.shift.id,
            products: bundle.products.map((p) => ({
              id: p.id,
              name: p.name,
            })),
          })
          ipqcStore.setReadOnlyMode(processSession.status === ShiftStatus.Ended)

          if (
            processSession.status === ShiftStatus.Ended &&
            typeof navigator !== "undefined" &&
            navigator.onLine
          ) {
            const shiftStartTime = sessionStore.shift.startedAt || undefined
            const shiftEndTime = sessionStore.shift.endedAt || undefined
            const hydratedSession = await hydrateOnlineIpqcSession(
              shiftStartTime,
              shiftEndTime,
              { persistOfflineSession: false }
            )

            if (hydratedSession?.sessionId) {
              await navigate("/ipqc/form")
              return
            }
          }

          if (
            processSession.status !== ShiftStatus.Ended &&
            !ipqcStore.offline.activeSession
          ) {
            if (!ipqcStore.offline.hydrated) {
              ipqcStore.setShiftDetails({
                processId,
                shiftId: sessionStore.shift.id,
                products: bundle.products.map((p) => ({
                  id: p.id,
                  name: p.name,
                })),
              })
              await navigate("/ipqc/form")
              return
            }

            if (typeof navigator !== "undefined" && navigator.onLine) {
              ipqcStore.setShiftDetails({
                processId,
                shiftId: sessionStore.shift.id,
                products: bundle.products.map((p) => ({
                  id: p.id,
                  name: p.name,
                })),
              })

              const shiftStartTime = sessionStore.shift.startedAt || undefined
              const shiftEndTime = sessionStore.shift.endedAt || undefined
              const hydratedSession = await hydrateOnlineIpqcSession(
                shiftStartTime,
                shiftEndTime
              )
              if (hydratedSession?.sessionId) {
                await navigate("/ipqc/form")
                return
              }
            }

            ipqcStore.setShiftDetails({
              processId,
              shiftId: sessionStore.shift.id,
              products: bundle.products.map((p) => ({
                id: p.id,
                name: p.name,
              })),
            })
            await navigate("/ipqc/select-products")
            return
          }

          await navigate("/ipqc/form")
        }
      } else if (processSession.processName === "Boilout") {
        let bundle
        try {
          bundle = await ensureBoiloutOfflineBundleAvailable({
            isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
          })
        } catch (err) {
          toast.error(getBoiloutOfflineBundleErrorMessage(err))
          return
        }

        if (processSession.status === ShiftStatus.Inactive) {
          try {
            boiloutStore.setShiftDetails({
              processId: processSession.processId,
              shiftId: sessionStore.shift.id,
              products: bundle.products.map((p) => ({
                id: p.id,
                name: p.name,
              })),
            })
            startProcess(processId)
            await navigate("/boilout/select-products")
          } catch (err) {
            console.error("Failed to prepare offline Boilout bundle:", err)
            throw err
          }
        } else {
          boiloutStore.setShiftDetails({
            id: sessionStore.shift.id,
            processId,
            shiftId: sessionStore.shift.id,
            products: bundle.products.map((p) => ({
              id: p.id,
              name: p.name,
            })),
          })
          boiloutStore.setReadOnlyMode(
            processSession.status === ShiftStatus.Ended
          )

          if (
            processSession.status === ShiftStatus.Ended &&
            typeof navigator !== "undefined" &&
            navigator.onLine
          ) {
            const shiftStartTime = sessionStore.shift.startedAt || undefined
            const shiftEndTime = sessionStore.shift.endedAt || undefined
            const hydratedSession = await hydrateOnlineBoiloutSession(
              shiftStartTime,
              shiftEndTime,
              { persistOfflineSession: false }
            )

            if (hydratedSession?.sessionId) {
              await navigate("/boilout/form")
              return
            }
          }

          if (
            processSession.status !== ShiftStatus.Ended &&
            !boiloutStore.offline.activeSession
          ) {
            if (!boiloutStore.offline.hydrated) {
              boiloutStore.setShiftDetails({
                processId,
                shiftId: sessionStore.shift.id,
                products: bundle.products.map((p) => ({
                  id: p.id,
                  name: p.name,
                })),
              })
              await navigate("/boilout/form")
              return
            }

            if (typeof navigator !== "undefined" && navigator.onLine) {
              boiloutStore.setShiftDetails({
                processId,
                shiftId: sessionStore.shift.id,
                products: bundle.products.map((p) => ({
                  id: p.id,
                  name: p.name,
                })),
              })

              const shiftStartTime = sessionStore.shift.startedAt || undefined
              const shiftEndTime = sessionStore.shift.endedAt || undefined
              const hydratedSession = await hydrateOnlineBoiloutSession(
                shiftStartTime,
                shiftEndTime
              )
              if (hydratedSession?.sessionId) {
                await navigate("/boilout/form")
                return
              }
            }

            boiloutStore.setShiftDetails({
              processId,
              shiftId: sessionStore.shift.id,
              products: bundle.products.map((p) => ({
                id: p.id,
                name: p.name,
              })),
            })
            await navigate("/boilout/select-products")
            return
          }

          await navigate("/boilout/form")
        }
      } else if (processSession.processName === "Packaging") {
        if (!packagingStore.allDepartments.length) {
          await loadAllDeptSchemas()
        }

        if (processSession.status === ShiftStatus.Inactive) {
          packagingStore.setProcessDetails({
            processId,
            shiftId: sessionStore.shift.id,
          })
          startProcess(processId)
          await navigate("/select-departments")
        } else {
          await navigate("/packaging/shift")
        }
      } else if (processSession.processName === "Packaging V2") {
        if (!packagingV2Store.allDepartments.length) {
          await loadAllV2DeptSchemas()
        }

        if (processSession.status === ShiftStatus.Inactive) {
          packagingV2Store.clearFormData()
          packagingV2Store.setProcessDetails({
            processId,
            shiftId: sessionStore.shift.id,
          })
          startProcess(processId)
          await navigate("/select-departments")
        } else {
          await syncAndNavigateToProcess({
            localProcessId: packagingV2Store.shift.processId,
            processId,
            hasLocalData: packagingV2Store.formSchemas.length > 0,
            clearFormData: packagingV2Store.clearFormData,
            setProcessDetails: () =>
              packagingV2Store.setProcessDetails({
                processId,
                shiftId: sessionStore.shift.id,
              }),
            fetchSheets: () => getSessionSheets(processSession.id),
            hydrateSheets: (data) =>
              packagingV2Store.hydrateFromPendingSheets(data),
            navigatePath: "/packaging-v2",
            errorLogPrefix: "Failed to fetch sheets:",
          })
        }
      } else if (
        processSession.processName === "Raw Material and Packaging Material"
      ) {
        const hasRMPMProducts =
          rmpmStore.allProducts.pmProducts.length > 0 &&
          rmpmStore.allProducts.rmProducts.length > 0

        if (!hasRMPMProducts) {
          await getRMPMProducts()
        }

        if (processSession.status === ShiftStatus.Inactive) {
          rmpmStore.setProcessDetails({
            processId,
            shiftId: sessionStore.shift.id,
          })
          await navigate("/rm-pm/onboard")
        } else {
          const hasLocalData =
            rmpmStore.trucks.rm.length > 0 || rmpmStore.trucks.pm.length > 0

          await syncAndNavigateToProcess({
            localProcessId: rmpmStore.process.processId,
            processId,
            hasLocalData,
            clearFormData: rmpmStore.clearFormData,
            setProcessDetails: () =>
              rmpmStore.setProcessDetails({
                processId,
                shiftId: sessionStore.shift.id,
              }),
            fetchSheets: () => getRMPMSessionSheets(processSession.id),
            hydrateSheets: hydrateRMPMFromSessionSheets,
            navigatePath: "/rm-pm/",
            errorLogPrefix: "Failed to fetch RM/PM sheets:",
          })
        }
      } else {
        throw new Error("Invalid process selected")
      }
    } catch (err) {
      console.error(err)
      posthog.captureException(err)
      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error("Cannot start process")
    }
  }

  function startProcess(processId: string) {
    setTimeout(() => {
      sessionStore.startProcess(processId)
    }, 500)
  }

  interface SyncAndNavigateOptions<T> {
    localProcessId: string
    processId: string
    hasLocalData: boolean
    clearFormData: () => void
    setProcessDetails: () => void
    fetchSheets: () => Promise<T>
    hydrateSheets: (data: T) => void
    navigatePath: string
    errorLogPrefix: string
  }

  async function syncAndNavigateToProcess<T extends { sheets: unknown[] }>(
    options: SyncAndNavigateOptions<T>
  ): Promise<void> {
    const {
      localProcessId,
      processId,
      hasLocalData,
      clearFormData,
      setProcessDetails,
      fetchSheets,
      hydrateSheets,
      navigatePath,
      errorLogPrefix,
    } = options

    const shouldClearData = localProcessId && localProcessId !== processId

    if (!hasLocalData || shouldClearData) {
      if (shouldClearData) {
        clearFormData()
      }
      try {
        const sheetsData = await fetchSheets()
        if (sheetsData.sheets.length > 0) {
          hydrateSheets(sheetsData)
        }
      } catch (err) {
        console.error(errorLogPrefix, err)
        throw err
      }
    }

    // Only set process details and navigate AFTER successful API call
    setProcessDetails()
    await navigate(navigatePath)
  }

  async function loadAllDeptSchemas() {
    const depts = await getAllDeptSchemas()
    packagingStore.setAllSchemas(depts)
  }

  async function loadAllV2DeptSchemas() {
    const response = await getAllV2DeptSchemas()
    packagingV2Store.setAllSchemas(response)
  }

  function currentShift() {
    return `${sessionStore.shift.name} (${formatShiftTime(sessionStore.shift.startedAt)} - ${formatShiftTime(sessionStore.shift.endedAt)})`
  }

  async function handleAddProcess(processIds: string[]) {
    await addProcesses(processIds)
  }

  async function handleTriggerEndShift() {
    try {
      const localIpqcProcess = sessionStore.session.processes.find(
        (process) => process.processName === "In-Process QC"
      )
      const localBoiloutProcess = sessionStore.session.processes.find(
        (process) => process.processName === "Boilout"
      )
      const hasPendingIpqcFinalize =
        !!ipqcStore.offline.activeSession?.pendingFinalizeAt &&
        localIpqcProcess?.status === ShiftStatus.Ended
      const hasPendingBoiloutFinalize =
        !!boiloutStore.offline.activeSession?.pendingFinalizeAt &&
        localBoiloutProcess?.status === ShiftStatus.Ended

      if (
        ipqcStore.offline.activeSession &&
        localIpqcProcess &&
        !hasPendingIpqcFinalize
      ) {
        setSheetText({
          title: "End Shift Error",
          text: "Please end the IPQC process before ending the shift.",
        })
        setCurrSheet(ManageProcessesSheets.EndShiftError)
        return
      }

      if (
        !ipqcStore.offline.activeSession &&
        localIpqcProcess &&
        localIpqcProcess.status !== ShiftStatus.Ended
      ) {
        setSheetText({
          title: "End Shift Error",
          text: "Please end the IPQC process before ending the shift.",
        })
        setCurrSheet(ManageProcessesSheets.EndShiftError)
        return
      }

      if (
        boiloutStore.offline.activeSession &&
        localBoiloutProcess &&
        !hasPendingBoiloutFinalize
      ) {
        setSheetText({
          title: "End Shift Error",
          text: "Please end the Boilout process before ending the shift.",
        })
        setCurrSheet(ManageProcessesSheets.EndShiftError)
        return
      }

      if (
        !boiloutStore.offline.activeSession &&
        localBoiloutProcess &&
        localBoiloutProcess.status !== ShiftStatus.Ended
      ) {
        setSheetText({
          title: "End Shift Error",
          text: "Please end the Boilout process before ending the shift.",
        })
        setCurrSheet(ManageProcessesSheets.EndShiftError)
        return
      }

      const status = await getShiftStatus()
      const hasLocalEndedIpqcProcess = sessionStore.session.processes.some(
        (process) =>
          process.processName === "In-Process QC" &&
          process.status === ShiftStatus.Ended
      )

      if (!status.shiftSession?.employeeSessions?.length) {
        if (hasLocalEndedIpqcProcess) {
          await handleEndShift()
          return
        }

        setSheetText({
          title: "End Shift Error",
          text: "There are no started processes. Please start a process or discard the shift",
        })
        setCurrSheet(ManageProcessesSheets.EndShiftError)

        return
      }

      const hasOnlyEndedProcesses = status.shiftSession.employeeSessions.every(
        (process) => process.status === ShiftStatus.Ended
      )

      if (hasOnlyEndedProcesses) {
        await handleEndShift()
        return
      }

      let isAllSheetsSubmitted = true
      const rapmTruckData = rmpmStore.trucks
      const rmpmFormData = rmpmStore.forms
      const rmpmSubmittedSheetData = Object.keys(rmpmStore.submitted ?? {})

      const shiftRMPMData: ShiftRMPMData[] = getRMPMTransformData(rapmTruckData)

      for (const pmTypeData of shiftRMPMData) {
        for (const truck of pmTypeData.truck) {
          if (!rmpmSubmittedSheetData.includes(truck.sheetId)) {
            isAllSheetsSubmitted = true
            break
          }
        }
      }

      if (!isAllSheetsSubmitted) {
        setSheetText({
          title: "End Shift Error",
          text: "There are pending RMPM sheets. Please submit all sheets before ending the shift.",
        })
        setCurrSheet(ManageProcessesSheets.EndShiftError)
        return
      }

      const shiftRMPMFormPendingLines = getRMPMFormPendingLines(
        shiftRMPMData,
        rmpmFormData
      )
      const summaryItem = transformToSummary(shiftRMPMFormPendingLines)

      setSheetPendingLine(summaryItem)

      if (rmpmEmployees.length === 0) {
        const rmpmEmployeeList: SuggestedEmployee[] = rmpmStore.employees ?? []
        setRmPmEmployees(rmpmEmployeeList)
      }

      setSheetText({
        title: "End Shift",
        text: "Are you sure you want to end your shift?",
      })

      setCurrSheet(ManageProcessesSheets.EndShift)
    } catch (err) {
      posthog.captureException(err)

      if (handleOfflineActionError(err, { action: "end", target: "shift" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error("Error ending shift")
    }
  }

  function closeSheets() {
    setCurrSheet(ManageProcessesSheets.None)
  }

  async function handleEndShift() {
    try {
      const endedIpqcProcess = sessionStore.session.processes.find(
        (process) =>
          process.processName === "In-Process QC" &&
          process.status === ShiftStatus.Ended
      )
      const endedBoiloutProcess = sessionStore.session.processes.find(
        (process) =>
          process.processName === "Boilout" &&
          process.status === ShiftStatus.Ended
      )
      const hasPendingIpqcFinalize =
        !!ipqcStore.offline.activeSession?.pendingFinalizeAt &&
        ipqcStore.offline.activeSession.processId ===
          endedIpqcProcess?.processId
      const hasPendingBoiloutFinalize =
        !!boiloutStore.offline.activeSession?.pendingFinalizeAt &&
        boiloutStore.offline.activeSession.processId ===
          endedBoiloutProcess?.processId

      if (hasPendingIpqcFinalize && endedIpqcProcess) {
        if (!navigator.onLine) {
          toast.error(
            "Reconnect to the internet before ending the shift. IPQC must sync first."
          )
          return
        }

        await finalizePendingOfflineIpqcProcess()
      }

      if (hasPendingBoiloutFinalize && endedBoiloutProcess) {
        if (!navigator.onLine) {
          toast.error(
            "Reconnect to the internet before ending the shift. Boilout must sync first."
          )
          return
        }

        await finalizePendingOfflineBoiloutProcess()
      }

      await endShift()
      await navigate("/onboard-shift")
    } catch (err) {
      posthog.captureException(err)

      if (handleOfflineActionError(err, { action: "end", target: "shift" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error("Cannot end shift. Please contact support")
    }
  }

  function getRMPMTransformData(truckData: RawData): ShiftRMPMData[] {
    return Object.entries(truckData).map(([PMTYPE, trucks]) => ({
      PMTYPE,
      truck: (trucks as any[]).map((truck) => ({
        truckNumber: truck.options?.label ?? "",
        sheetId: truck.sheetId,
        products: (truck.properties?.products?.items ?? []).map((p: any) => ({
          name: p.options?.label ?? "",
          productId: p.id ?? "",
        })),
      })),
    }))
  }

  function objectToArrayProduct(productsObj: Record<string, any>) {
    return Object.entries(productsObj)
      .filter(([key]) => !isNaN(Number(key))) // keep only numeric keys
      .map(([, value]) => value)
  }

  function getRMPMFormPendingLines(
    shiftRMPMData: ShiftRMPMData[],
    formData: RawData
  ): ShiftRMPMData[] {
    return shiftRMPMData.map((item) => {
      const pmFormList = formData[item.PMTYPE] ?? []

      const updatedTrucks = item.truck.map((truck, index) => {
        const formTruck = pmFormList[index]
        if (!formTruck) return truck

        // --- Extract general pending lines
        const generalPendingLines: PendingLine[] = Object.values(
          formTruck.general ?? {}
        )
          .filter((g: any) => typeof g === "object" && g.value === "")
          .map((g: any) => ({
            parameterId: g.id,
            label: g.label,
          }))

        // --- Extract product pending lines
        const products: Product[] = objectToArrayProduct(
          formTruck.products ?? {}
        ).map((p: any) => {
          const pendingLines: PendingLine[] = (p.parameters ?? [])
            .filter(
              (param: any) => typeof param === "object" && param.value === ""
            )
            .map((param: any) => ({
              parameterId: param.id,
              label: param.label,
            }))

          return {
            name: p.label,
            productId: p.id,
            ...(pendingLines.length > 0 ? { pendingLines } : {}),
          }
        })

        return {
          ...truck,
          ...(generalPendingLines.length > 0 ? { generalPendingLines } : {}),
          products: products,
        }
      })

      return {
        ...item,
        truck: updatedTrucks,
      }
    })
  }

  function transformToSummary(data: ShiftRMPMData[]): RMPMPendingSummary[] {
    return data.flatMap(
      (d) =>
        d.truck
          .map((truck) => {
            const items: SummaryItem[] = []

            // --- General Pending Lines
            if (truck.generalPendingLines?.length) {
              const labels = truck.generalPendingLines
                .map((line) => line.label)
                .join(",")
              items.push({ name: "General Data", Pending: labels })
            }

            // --- Product Pending Lines
            truck.products?.forEach((product) => {
              if (product.pendingLines?.length) {
                const pending = product.pendingLines
                  .map((p) => p.label)
                  .join(",")
                items.push({ name: product.name, Pending: pending })
              }
            })

            if (items.length === 0) return null

            return {
              Name: `${d.PMTYPE.toUpperCase()} • Truck • ${truck.truckNumber}`,
              items,
            }
          })
          .filter((x): x is RMPMPendingSummary => x !== null) // remove nulls with type guard
    )
  }

  // Show loading during session recovery
  if (isLoadingSession) {
    return (
      <div className="h-screen overflow-auto">
        <Topbar
          className="sticky top-0 left-0"
          title="Unit Quality"
          subtitle={topbarSubtitle()}
        />
        <div className="px-4 py-4 space-y-4">
          <div className="rounded-lg bg-white p-4 shadow-xs space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-9 w-40" />
          </div>

          <div className="space-y-4">
            <div className="rounded-lg bg-white p-4 shadow-xs space-y-3">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
            <div className="rounded-lg bg-white p-4 shadow-xs space-y-3">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
            <div className="rounded-lg bg-white p-4 shadow-xs space-y-3">
              <Skeleton className="h-6 w-36" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen overflow-auto">
      <Topbar
        className="sticky top-0 left-0"
        title="Unit Quality"
        subtitle={topbarSubtitle()}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Icon name="MoreVertical" className="text-button-black h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent inPortal>
            <DropdownMenuItem onClick={handleTriggerEndShift}>
              End Shift
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleTriggerDiscardShift}>
              Discard and Exit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowLogoutSheet(true)}>
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Topbar>

      <AddProcessBar
        className="sticky top-17 left-0"
        shift={currentShift()}
        hideBtn={!canAddProcess()}
        onClickAdd={handleTriggerAddProcess}
      />

      {!sessionStore.session && (
        <div className="text-label-text flex justify-center">
          Please add a process
        </div>
      )}

      {sessionStore.session && (
        <div className="mt-4 px-4 flex flex-col gap-4">
          {dedupedProcesses.map((process: ProcessSession) => (
            <div key={process.id || process.processId}>
              <h2 className="text-button-black font-semibold text-2xl mb-1.5">
                {process.processName}
              </h2>

              <DraftCard
                processId={process.processId}
                status={process.status}
                plantName={userStore.plant.name}
                updatedAt={
                  process.status === ShiftStatus.Ended && process.endedAt
                    ? process.endedAt
                    : process.updatedAt
                }
                onClick={handleStartProcess}
              />
            </div>
          ))}

          {!dedupedProcesses.length && (
            <div className="text-label-text mt-10 text-center flex flex-col items-center gap-2">
              <div>Please add a process</div>
              <RoundedButton
                icon={<Icon name="Plus" className="size-4" />}
                onClick={handleTriggerAddProcess}
              >
                Process
              </RoundedButton>
            </div>
          )}
        </div>
      )}

      <SelectProcessSheet
        open={showProcessSheet}
        onOpenChange={setShowProcessSheet}
        onClickAdd={handleAddProcess}
      />

      <EndShiftSheet
        open={currSheet === ManageProcessesSheets.EndShift}
        pendingItems={sheetPendingLine?.map((line) => ({
          label: line.Name,
          subItems: line.items.map((item) => `${item.name}: ${item.Pending}`),
        }))}
        employees={rmpmEmployees}
        title={sheetText.title}
        description={sheetText.text}
        onClickBack={closeSheets}
        onClickConfirm={handleEndShift}
      />
      <EndShiftSheet
        open={currSheet === ManageProcessesSheets.DiscardShift}
        title={sheetText.title}
        description={sheetText.text}
        onClickBack={closeSheets}
        onClickConfirm={handleDiscardShift}
      />
      <EndShiftErrorSheet
        open={currSheet === ManageProcessesSheets.EndShiftError}
        title={sheetText.title}
        text={sheetText.text}
        onClickBack={closeSheets}
      />
      <LogoutConfirmSheet
        open={showLogoutSheet}
        onOpenChange={setShowLogoutSheet}
      />
    </div>
  )
}
